// assets/cabinet-options.js — Davenport cabinet options (2026-10-05).
// 1. Wood grain direction: shown + required only while a wood-grain finish is selected.
// 2. Add-ons: a select is only submitted (gets its name) once a value other than "None" is chosen.
// 3. While wood grain is required but unanswered, the express checkout buttons are paused so
//    the question can't be skipped; tapping them points the shopper at the question instead.
// 4. Priced add-ons (End Panel, Filler; 2026-10-13): a chosen add-on goes in the same /cart/add
//    request as its own line (items[] next to the cabinet's id), tagged with the cabinet's finish.
//    The end panel follows the cabinet depth. Express checkout would buy the cabinet alone, so with
//    add-ons chosen it adds everything to the cart and goes to checkout instead.
//    The "Your Configuration" card lists every chosen add-on and adds the priced ones to its total
//    (re-applied after configuration-summary.js swaps the card in on a variant change).
// Everything is delegated on document, so it survives Horizon's variant-picker morphs.

if (!window.__cabinetOptionsInit) {
  window.__cabinetOptionsInit = true;

  /** @param {Element} el */
  const sectionOf = (el) => el.closest('.shopify-section, dialog') || document;

  /** @param {HTMLElement} block */
  function selectedFinish(block) {
    const optName = block.dataset.finishOption || 'Select Finish';
    const scope = sectionOf(block);
    const pickers = [...scope.querySelectorAll('variant-picker')].filter((p) => !p.closest('product-card'));
    for (const picker of pickers) {
      const radio = [...picker.querySelectorAll('input[type="radio"]:checked')].find((i) =>
        i.name.startsWith(optName + '-')
      );
      if (radio) return radio.dataset.optionValueName || radio.value;
      const select = [...picker.querySelectorAll('select')].find((s) => s.name && s.name.startsWith(optName + '-'));
      if (select && select.selectedOptions[0]) return select.selectedOptions[0].dataset.optionValueName || select.value;
    }
    return null;
  }

  /** @param {HTMLElement} block */
  function syncGrain(block) {
    const finish = selectedFinish(block);
    if (finish == null) return; // picker not found: keep server-rendered state
    const woods = (block.dataset.woodFinishes || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const isWood = woods.includes(finish.trim().toLowerCase());
    const select = /** @type {HTMLSelectElement|null} */ (block.querySelector('select'));
    block.hidden = !isWood;
    if (select) select.disabled = !isWood;
    syncOrder(block);
    syncExpress(block);
  }

  // Phones reorder the product blocks with CSS `order` (title 1, picker 2, ...). Keep the question
  // glued to whatever finish picker sits right before it (variant picker or configurator).
  /** @param {HTMLElement} block */
  function syncOrder(block) {
    let prev = block.previousElementSibling;
    while (prev && (prev.tagName === 'SCRIPT' || prev.tagName === 'STYLE')) prev = prev.previousElementSibling;
    if (prev) block.style.order = getComputedStyle(prev).order;
    else block.style.removeProperty('order');
  }

  /** @param {HTMLElement} block */
  function syncExpress(block) {
    const select = /** @type {HTMLSelectElement|null} */ (block.querySelector('select'));
    const formId = select?.getAttribute('form');
    const form = formId ? document.getElementById(formId) : null;
    const holder = form?.closest('product-form-component');
    if (!holder) return;
    const needs = !!select && !select.disabled && !select.value;
    holder.toggleAttribute('data-needs-grain', needs);
  }

  function syncAll() {
    document.querySelectorAll('[data-wood-grain]').forEach((b) => syncGrain(/** @type {HTMLElement} */ (b)));
  }

  /** @param {HTMLSelectElement} select */
  function syncAddonName(select) {
    const name = select.dataset.addonName;
    if (!name) return;
    if (select.value) select.setAttribute('name', name);
    else select.removeAttribute('name');
  }

  document.addEventListener('change', (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;
    if (t.matches('select[data-addon-name]')) syncAddonName(/** @type {HTMLSelectElement} */ (t));
    const grain = t.closest('[data-wood-grain]');
    if (grain) syncExpress(/** @type {HTMLElement} */ (grain));
    if (t.closest('variant-picker')) {
      syncAll();
      setTimeout(syncAll, 0);
    }
  });

  // After the picker morph (finish/size change) re-read the selected finish.
  document.addEventListener('variant:update', () => setTimeout(syncAll, 0));
  const syncOrders = () =>
    document.querySelectorAll('[data-wood-grain]').forEach((b) => syncOrder(/** @type {HTMLElement} */ (b)));
  window.addEventListener('resize', syncOrders);
  window.addEventListener('load', syncOrders); // late stylesheets (e.g. configurator.css) can change the picker's order

  // Express checkout click while wood grain is unanswered → point at the question instead.
  document.addEventListener(
    'click',
    (event) => {
      const t = event.target;
      if (!(t instanceof Element)) return;
      const express = t.closest('.accelerated-checkout-block');
      const holder = express?.closest('product-form-component[data-needs-grain]');
      if (!holder) return;
      const section = sectionOf(holder);
      const select = /** @type {HTMLSelectElement|null} */ (
        section.querySelector('[data-wood-grain]:not([hidden]) select')
      );
      if (!select) return;
      event.preventDefault();
      event.stopPropagation();
      select.scrollIntoView({ block: 'center' });
      select.focus();
      select.reportValidity();
    },
    true
  );

  // ---- Priced add-ons -------------------------------------------------------------------------

  /** @type {Map<string, any>} last variant JSON seen per product id (from variant:update) */
  const variantByProduct = new Map();

  /** The cabinet variant the form will add: the picker's variant JSON, matched to input[name=id]. */
  /** @param {HTMLFormElement} form */
  function cabinetVariant(form) {
    const id = /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="id"]'))?.value;
    const holder = form.closest('product-form-component');
    const cached = holder instanceof HTMLElement ? variantByProduct.get(holder.dataset.productId || '') : null;
    if (cached && (!id || String(cached.id) === String(id))) return cached;
    for (const script of sectionOf(form).querySelectorAll('script[type="application/json"]')) {
      const picker = script.parentElement?.closest('variant-picker, swatches-variant-picker-component');
      if (!picker || picker.closest('product-card')) continue;
      try {
        const v = JSON.parse(script.textContent || '');
        if (v && v.id && (!id || String(v.id) === String(id))) return v;
      } catch {}
    }
    return null;
  }

  /** @param {any} variant */
  function depthOf(variant) {
    const m = String(variant?.title || '').match(/D:\s*([\d.]+)/);
    return m ? parseFloat(m[1]) : null;
  }

  /** Show the end panel whose width is the narrowest at least 1.5" deeper than the cabinet. */
  /** @param {HTMLElement} block @param {any} variant */
  function syncPanels(block, variant) {
    const panels = /** @type {HTMLElement[]} */ ([...block.querySelectorAll('[data-addon-panel]')]);
    if (panels.length < 2) return;
    const depth = depthOf(variant);
    if (depth == null) return;
    const byWidth = panels.slice().sort((a, b) => parseFloat(a.dataset.panelWidth || '0') - parseFloat(b.dataset.panelWidth || '0'));
    const target = byWidth.find((p) => parseFloat(p.dataset.panelWidth || '0') >= depth + 1.5) || byWidth[byWidth.length - 1];
    const current = panels.find((p) => !p.hidden);
    if (!target || target === current) return;
    const qty = current?.querySelector('select')?.selectedOptions[0]?.dataset.qty || '';
    for (const panel of panels) {
      const select = /** @type {HTMLSelectElement|null} */ (panel.querySelector('select'));
      const show = panel === target;
      panel.hidden = !show;
      if (!select) continue;
      select.disabled = !show;
      const keep = show && qty ? [...select.options].find((o) => o.dataset.qty === qty) : null;
      select.value = keep ? keep.value : '';
    }
    syncAddonFlag(block);
  }

  /** @param {Element} el */
  function addonSelectsFor(el) {
    const form = el.closest('form') || document.getElementById(el.getAttribute('form') || '');
    // getAttribute: the product form has an <input name="id">, which shadows form.id.
    const formId = form?.getAttribute('id');
    if (!formId) return [];
    return /** @type {HTMLSelectElement[]} */ (
      [...document.querySelectorAll('select[data-addon-line]')].filter((s) => s.getAttribute('form') === formId)
    );
  }

  /** Marks the product form while a priced add-on is chosen (express checkout reroutes then). */
  /** @param {Element} el */
  function syncAddonFlag(el) {
    const select = el.matches('select') ? el : el.querySelector('select[data-addon-line]');
    const formId = select?.getAttribute('form');
    const form = formId ? document.getElementById(formId) : null;
    const holder = form?.closest('product-form-component');
    if (!form || !holder) return;
    holder.toggleAttribute('data-has-addons', addonSelectsFor(form).some((s) => !s.disabled && s.value));
  }

  // new FormData(productForm) in product-form.js fires this: append the chosen add-ons as items[].
  document.addEventListener(
    'formdata',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !event.formData.get('id')) return;
      const chosen = addonSelectsFor(form).filter((s) => !s.disabled && s.value);
      if (!chosen.length) return;
      const variant = cabinetVariant(form);
      const block = /** @type {HTMLElement|null} */ (chosen[0].closest('[data-cabinet-addons]'));
      const options = Array.isArray(variant?.options) ? variant.options : [];
      const finish = options[Number(block?.dataset.finishIndex)] ?? '';
      const size = options[Number(block?.dataset.sizeIndex)] ?? '';
      const title = block?.dataset.productTitle || '';
      const grain = event.formData.get('properties[Wood Grain Direction]');
      chosen.forEach((select, i) => {
        const opt = select.selectedOptions[0];
        if (!opt?.dataset.variantId || !opt.dataset.qty) return;
        event.formData.append(`items[${i}][id]`, opt.dataset.variantId);
        event.formData.append(`items[${i}][quantity]`, opt.dataset.qty);
        if (finish) event.formData.append(`items[${i}][properties][Finish]`, finish);
        if (grain) event.formData.append(`items[${i}][properties][Wood Grain Direction]`, String(grain));
        if (title) event.formData.append(`items[${i}][properties][For]`, size ? `${title} (${size})` : title);
      });
    },
    true
  );

  document.addEventListener('change', (event) => {
    const t = event.target;
    if (t instanceof HTMLSelectElement && t.matches('select[data-addon-line]')) syncAddonFlag(t);
    const block = t instanceof Element ? t.closest('[data-cabinet-addons]') : null;
    if (block) syncSummary(/** @type {HTMLElement} */ (block));
  });

  document.addEventListener('variant:update', (event) => {
    const detail = /** @type {any} */ (event).detail;
    const variant = detail?.resource;
    if (!variant) {
      syncSummaries();
      return;
    }
    if (detail?.data?.productId) variantByProduct.set(String(detail.data.productId), variant);
    const target = event.target instanceof Element ? event.target : document;
    sectionOf(target).querySelectorAll('[data-cabinet-addons]').forEach((b) => {
      syncPanels(/** @type {HTMLElement} */ (b), variant);
      syncSummary(/** @type {HTMLElement} */ (b));
    });
  });

  // Express checkout with add-ons chosen: add cabinet + add-ons to the cart, then go to checkout.
  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented) return; // wood grain question still open (handled above)
      const t = event.target;
      if (!(t instanceof Element)) return;
      const express = t.closest('.accelerated-checkout-block');
      const holder = express?.closest('product-form-component[data-has-addons]');
      const form = holder?.querySelector('form');
      if (!form) return;
      event.preventDefault();
      event.stopPropagation();
      if (holder.hasAttribute('data-addons-busy')) return;
      holder.setAttribute('data-addons-busy', '');
      const root = /** @type {any} */ (window).Shopify?.routes?.root || '/';
      fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) })
        .then((r) => r.json())
        .then((r) => {
          window.location.href = r && r.status ? `${root}cart` : `${root}checkout`;
        })
        .catch(() => {
          window.location.href = `${root}cart`;
        })
        .finally(() => holder.removeAttribute('data-addons-busy'));
    },
    true
  );

  // ---- "Your Configuration" card: add-on rows + total -----------------------------------------

  /** @param {number} cents @param {boolean} [short] whole amounts without ".00" (matches the dropdowns) */
  function money(cents, short) {
    const digits = short && cents % 100 === 0 ? 0 : 2;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: document.documentElement.dataset.currency || 'USD',
        minimumFractionDigits: digits,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch {
      return '$' + (cents / 100).toFixed(digits);
    }
  }

  /** @param {HTMLElement} block */
  function syncSummary(block) {
    const summaries = [...sectionOf(block).querySelectorAll('configuration-summary')].filter((s) => !s.closest('product-card'));
    if (!summaries.length) return;
    const quote = block.dataset.quoteLabel || 'quoted after order';
    /** @type {Array<[string, string]>} */
    const rows = [];
    let extra = 0;
    for (const select of block.querySelectorAll('select[data-addon-label]')) {
      if (!(select instanceof HTMLSelectElement) || select.disabled || !select.value) continue;
      const opt = select.selectedOptions[0];
      if (!opt) continue;
      if (select.matches('[data-addon-line]')) {
        const qty = Number(opt.dataset.qty) || 0;
        const cents = qty * (Number(opt.dataset.price) || 0);
        extra += cents;
        rows.push([select.dataset.addonLabel || '', `${opt.dataset.size || ''}, Qty ${qty} (+${money(cents, true)})`]);
      } else {
        rows.push([select.dataset.addonLabel || '', `${opt.value} (${quote})`]);
      }
    }
    for (const summary of summaries) {
      const dl = summary.querySelector('[ref="rows"]');
      if (dl) {
        dl.querySelectorAll('[data-addon-row]').forEach((r) => r.remove());
        for (const [term, value] of rows) {
          const row = document.createElement('div');
          row.className = 'configuration-summary__row';
          row.dataset.addonRow = '';
          const dt = document.createElement('dt');
          dt.className = 'configuration-summary__term';
          dt.textContent = term;
          const dd = document.createElement('dd');
          dd.className = 'configuration-summary__value';
          dd.textContent = value;
          row.append(dt, dd);
          if (dl.classList.contains('configuration-summary__rows--editable')) {
            // Invisible stand-in for the Edit column so the values line up with the rows above.
            const pad = document.createElement('span');
            pad.className = 'configuration-summary__edit';
            pad.style.visibility = 'hidden';
            pad.setAttribute('aria-hidden', 'true');
            pad.textContent = summary.querySelector('.configuration-summary__edit')?.textContent || 'Edit';
            row.append(pad);
          }
          dl.append(row);
        }
      }
      const total = /** @type {HTMLElement|null} */ (summary.querySelector('[ref="totalValue"][data-base-price]'));
      const base = Number(total?.dataset.basePrice);
      if (total && Number.isFinite(base)) total.textContent = money(base + extra);
    }
  }

  const syncSummaries = () =>
    document.querySelectorAll('[data-cabinet-addons]').forEach((b) => syncSummary(/** @type {HTMLElement} */ (b)));

  const init = () => {
    document.querySelectorAll('select[data-addon-name]').forEach((s) => syncAddonName(/** @type {HTMLSelectElement} */ (s)));
    document.querySelectorAll('[data-cabinet-addons]').forEach((b) => syncAddonFlag(b));
    syncSummaries(); // back/forward cache can restore chosen add-ons
    syncAll();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  const style = document.createElement('style');
  style.textContent =
    'product-form-component[data-needs-grain] .accelerated-checkout-block{opacity:.45}' +
    'product-form-component[data-needs-grain] .accelerated-checkout-block > *{pointer-events:none}' +
    'product-form-component[data-has-addons] .accelerated-checkout-block{cursor:pointer}' +
    'product-form-component[data-has-addons] .accelerated-checkout-block > *{pointer-events:none}' +
    'product-form-component[data-addons-busy] .accelerated-checkout-block{opacity:.6}';
  document.head.appendChild(style);
}
